import type { InspectorDTO } from 'src/types/dto/inspector.dto';
import type {
  RescheduleProgressAllocatedInspectorDTO,
  RescheduleProgressChangeDTO,
} from 'src/types/dto/reschedule-progress.dto';
import type { RescheduleChangeDTO, RescheduleSuggestionDTO } from 'src/types/dto/reschedule-suggestion.dto';

const normalizeAllocatedInspector = (
  allocatedInspector: RescheduleProgressAllocatedInspectorDTO
): InspectorDTO => {
  if (!allocatedInspector.inspector) {
    return allocatedInspector as unknown as InspectorDTO;
  }

  return {
    ...allocatedInspector.inspector,
    travelDistance:
      allocatedInspector.travelDistance ?? allocatedInspector.inspector.travelDistance,
    slots: allocatedInspector.slot ?? allocatedInspector.inspector.slots,
    solutionAppointments:
      allocatedInspector.solutionAppointments ??
      allocatedInspector.inspector.solutionAppointments,
  };
};

export const toLegacyRescheduleChange = (
  change: RescheduleProgressChangeDTO
): RescheduleChangeDTO => ({
  activity: change.activity,
  oldAppointment: change.oldAppointments,
  newAppointment: change.newAllocatedInspectors.flatMap((allocatedInspector) =>
    (allocatedInspector.solutionAppointments ?? []).map((appointment) => ({
      ...appointment,
      activity: change.activity,
      hotelNights:
        typeof appointment.hotelNights === 'number'
          ? String(appointment.hotelNights)
          : appointment.hotelNights,
    }))
  ),
});

const getFirstAllocatedInspector = (
  changes: RescheduleProgressChangeDTO[]
): RescheduleProgressAllocatedInspectorDTO | undefined =>
  changes
    .flatMap((change) => change.newAllocatedInspectors)
    .find((allocatedInspector) => Boolean(allocatedInspector));

const mergeRescheduleChange = (
  existing: RescheduleChangeDTO | undefined,
  incoming: RescheduleChangeDTO
): RescheduleChangeDTO => {
  if (!existing) {
    return incoming;
  }

  return {
    ...incoming,
    activity:
      existing.activity.status !== undefined && incoming.activity.status === undefined
        ? existing.activity
        : incoming.activity,
    newAppointment: incoming.newAppointment.length
      ? incoming.newAppointment
      : existing.newAppointment,
    oldAppointment: incoming.oldAppointment?.length
      ? incoming.oldAppointment
      : existing.oldAppointment,
  };
};

const mergeInspector = (
  existing: InspectorDTO | undefined,
  incoming: InspectorDTO | undefined
): InspectorDTO => {
  if (!existing) {
    return incoming as InspectorDTO;
  }

  if (!incoming) {
    return existing;
  }

  if (incoming.name) {
    return {
      ...incoming,
      authorizations:
        incoming.authorizations?.length || !existing.authorizations?.length
          ? incoming.authorizations
          : existing.authorizations,
    };
  }

  return {
    ...existing,
    ...incoming,
    name: existing.name,
    authorizations: existing.authorizations?.length
      ? existing.authorizations
      : incoming.authorizations,
  };
};

export const mergeRescheduleSuggestion = (
  existing: RescheduleSuggestionDTO | undefined,
  incoming: RescheduleSuggestionDTO
): RescheduleSuggestionDTO => {
  if (!existing) {
    return incoming;
  }

  const changeOrder = existing.changes.map((change) => change.activity.id);
  const changesByActivityId = new Map(
    existing.changes.map((change) => [change.activity.id, change])
  );

  incoming.changes.forEach((incomingChange) => {
    const activityId = incomingChange.activity.id;

    if (!changesByActivityId.has(activityId)) {
      changeOrder.push(activityId);
    }

    changesByActivityId.set(
      activityId,
      mergeRescheduleChange(changesByActivityId.get(activityId), incomingChange)
    );
  });

  return {
    solutionId: incoming.solutionId,
    inspector: mergeInspector(existing.inspector, incoming.inspector),
    changes: changeOrder.map((activityId) => changesByActivityId.get(activityId)!),
  };
};

export const groupRescheduleProgressChanges = (
  changes: RescheduleProgressChangeDTO[]
): RescheduleSuggestionDTO[] => {
  const groups = new Map<number, RescheduleProgressChangeDTO[]>();

  changes.forEach((change) => {
    const existing = groups.get(change.solutionId);
    if (existing) {
      existing.push(change);
    } else {
      groups.set(change.solutionId, [change]);
    }
  });

  return Array.from(groups.entries())
    .map(([solutionId, groupedChanges]) => {
      const allocatedInspector = getFirstAllocatedInspector(groupedChanges);
      if (!allocatedInspector) {
        return null;
      }

      return {
        solutionId,
        inspector: normalizeAllocatedInspector(allocatedInspector),
        changes: groupedChanges.map(toLegacyRescheduleChange),
      };
    })
    .filter((suggestion): suggestion is RescheduleSuggestionDTO => suggestion !== null);
};

export const mergeRescheduleSuggestions = (
  current: RescheduleSuggestionDTO[],
  incoming: RescheduleSuggestionDTO[]
): RescheduleSuggestionDTO[] => {
  if (incoming.length === 0) {
    return current;
  }

  const merged = new Map(
    current.map((item) => [item.solutionId, item] as const)
  );
  const order = current.map((item) => item.solutionId);

  incoming.forEach((item) => {
    if (!merged.has(item.solutionId)) {
      order.push(item.solutionId);
    }

    merged.set(
      item.solutionId,
      mergeRescheduleSuggestion(merged.get(item.solutionId), item)
    );
  });

  return order
    .map((solutionId) => merged.get(solutionId))
    .filter((suggestion): suggestion is RescheduleSuggestionDTO => suggestion !== undefined);
};
